import type { PublicClientApplication } from '@azure/msal-browser';
import type { JwtPayload } from 'jwt-decode';
import { jwtDecode } from 'jwt-decode';
import { USER_ID_NOT_SET_ERROR } from 'core/constants/error-http.constants';
import { DEFAULT_MSAL_LOGIN_REDIRECT_PARAMETERS, LOCAL_STORAGE_TOKEN_KEY } from 'core/constants/msal.constants';
import { resetStoreAction } from 'core/store/actions';
import { dispatchAction } from 'core/store/store.utils';
import { LOCAL_STORAGE_APP_INSTANCE_ID_KEY } from 'core/utils/app-instance-id.utils';
import { LOCAL_STORAGE_CHANNEL_KEY } from 'core/utils/channel.utils';
import { clearLocalStorageExcept } from 'screens/authentication/logout.utils';
import { resetUserPropertiesReady } from './gtm-events/gtm.events';
import { extractSearchParams } from './url.utils';

let currentRequest: Promise<string> | null = null;

type TDecodedTokenPayload = JwtPayload & {
  userId?: string;
};

const incrementTokenVersion = () => {
  try {
    dispatchAction({ type: 'auth/incrementTokenVersion' });
  } catch {
    // eslint-disable-next-line no-console
    console.warn('Store might not be initialized yet');
  }
};

export const getTokenFromLocalStorage = () => localStorage.getItem(LOCAL_STORAGE_TOKEN_KEY);
export const saveTokenToLocalStorage = (token: string) => {
  localStorage.setItem(LOCAL_STORAGE_TOKEN_KEY, token);
  incrementTokenVersion();
};

export const decodeJWTToken = (token: string) => {
  if (!token) return null;
  try {
    const payload = jwtDecode<TDecodedTokenPayload>(token);
    return payload;
  } catch {
    return null;
  }
};

const isTokenExpired = (token: string) => {
  const payload = jwtDecode(token);
  const expirationTime = payload.exp;
  const currentUnixTime = Math.floor(Date.now() / 1000);

  if (!expirationTime) return true;
  return expirationTime <= currentUnixTime;
};

const getTokenOrRedirect = async (msalInstance: PublicClientApplication) => {
  const currentToken = getTokenFromLocalStorage();
  const { userId } = decodeJWTToken(currentToken ?? '') ?? {};

  if (userId && currentToken && !isTokenExpired(currentToken)) return currentToken;

  const activeAccount = msalInstance.getActiveAccount();
  const accounts = msalInstance.getAllAccounts();

  if (!activeAccount && accounts.length === 0) throw new Error(`active account not found, can't generate token`);

  const account = activeAccount || accounts[0];

  const defaultConfig = {
    ...DEFAULT_MSAL_LOGIN_REDIRECT_PARAMETERS,
    account,
  };

  await msalInstance.handleRedirectPromise();

  try {
    if (!userId) throw new Error(USER_ID_NOT_SET_ERROR);

    const token = await msalInstance.ssoSilent(defaultConfig);
    saveTokenToLocalStorage(token.accessToken);
    return token.accessToken;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(`sso silent fails ${error}, using login redirect with prompt: none as fallback`);
    const searchParams = extractSearchParams();
    await msalInstance.handleRedirectPromise();
    const preserved = clearLocalStorageExcept([LOCAL_STORAGE_CHANNEL_KEY, LOCAL_STORAGE_APP_INSTANCE_ID_KEY]);
    const { message: errorMessage } = error as Error;
    resetUserPropertiesReady();
    dispatchAction(resetStoreAction());
    await msalInstance.loginRedirect({
      ...defaultConfig,
      ...(errorMessage === USER_ID_NOT_SET_ERROR ? { prompt: 'login' } : {}),
      extraQueryParameters: {
        ...searchParams,
        ...defaultConfig.extraQueryParameters,
        channelId: preserved[LOCAL_STORAGE_CHANNEL_KEY] ?? '',
        app_instance_id: preserved[LOCAL_STORAGE_APP_INSTANCE_ID_KEY] ?? '',
        ...(errorMessage === USER_ID_NOT_SET_ERROR ? { error: errorMessage } : {}),
      },
    });
    throw new Error(`sso silent fails ${error}, using login redirect with prompt: none as fallback`);
  }
};

export const acquireAccessToken = async (msalInstance: PublicClientApplication) => {
  if (!currentRequest) {
    const response = getTokenOrRedirect(msalInstance);
    currentRequest = response;
  }

  const result = await currentRequest;
  currentRequest = null;
  return result;
};
